import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getToken } from "firebase/messaging";
import { useNavigate } from "react-router-dom";
import { injectIntl } from "react-intl";
import * as yup from "yup";
import { useFormik } from "formik";
import { Alert, Box, Button, Card, CardActions, CardContent, Grid2 as Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { Cancel as CancelIcon } from "@mui/icons-material";
import { LoadingSpinner } from "../";
import {
  getConfig,
  NOTIFICHE_NON_IMPOSTATE,
  NOTIFICHE_CONCESSE,
  NOTIFICHE_NEGATE,
  CIFRE_ID_PAZIENTE_JSON,
  WELCOME_PAGE_VISITED,
  NOTIFICATION_PERMISSION_SETTED,
} from "../../modules/configuration";
import {
  HOME_INFO,
  CODICE_ACCESSO_PLACEHOLDER,
  REQUIRED_FIELD,
  CODICE_ACCESSO_ERROR,
  CODICE_ACCESSO_ERROR_2,
  CERCA,
  CLEAR,
  NOTIFICHE_NEGATE_ALERT,
} from "../../modules/language";
import { fetchMonitoraggio, getMonitoraggio, fetchMonitoraggioEnded, IS_NAVIGATION_FROM_FORM, CODICE_EVENTO } from "../../modules/tracking";
import { resetNotification } from "../../modules/notification";
import { ID_PAZIENTE } from "../../modules/prontoSoccorso";
import { PATH_MONITORAGGIO } from "../../path";
import { firebaseMessaging, getMessageString } from "../../utility";
import { WelcomePage } from "./WelcomePage";
import { Banners, GuidaInstallazione } from "../shared";

const H_HEADER = 120;
const H_HEADER_MOBILE = 80;
const H_FOOTER = 150;
const H_FOOTER_MOBILE = 80;

// TODO Tradurre parti relative alle notifiche ed alla welcome page

export const Home = injectIntl(({ intl }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showSpinner, setShowSpinner] = useState(false);
  const config = useSelector(getConfig);
  const monitoraggio = useSelector(getMonitoraggio);
  const monitoraggioStarted = useSelector(fetchMonitoraggioEnded);
  const fromSaluteSimplex = window.ReactNativeWebView != null;
  const [altezzaHeader, setAltezzaHeader] = useState(window.innerWidth < 1200 ? H_HEADER_MOBILE : H_HEADER);
  const [altezzaFooter, setAltezzaFooter] = useState(window.innerWidth < 1200 ? H_FOOTER_MOBILE : H_FOOTER);
  const [pageHeight, setPageHeight] = useState();
  const [notificationPermission, setNotificationPermission] = useState(window.Notification?.permission);
  const [showWelcomeModal, setShowWelcomeModal] = useState(!fromSaluteSimplex && localStorage.getItem(WELCOME_PAGE_VISITED) == null);

  useEffect(() => {
    // Aggiorno lo stato notifiche
    navigator.permissions?.query({ name: "notifications" }).then((permissionStatus) => {
      permissionStatus.onchange = () => setNotificationPermission(permissionStatus.state);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setAltezzaHeader(window.innerWidth < 1200 ? H_HEADER_MOBILE : H_HEADER);
      setAltezzaFooter(window.innerWidth < 1200 ? H_FOOTER_MOBILE : H_FOOTER);
    };
    const syncWithLocalStorage = () => {
      if (localStorage.getItem(WELCOME_PAGE_VISITED) == null) setShowWelcomeModal(true);
    };
    window.addEventListener("resize", handleResize);
    window.addEventListener("storage", syncWithLocalStorage);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("storage", syncWithLocalStorage);
    };
  }, []);

  useEffect(() => {
    const footerHeight = fromSaluteSimplex ? 0 : altezzaFooter;
    setPageHeight("calc(100% - " + altezzaHeader + "px - " + footerHeight + "px)");
  }, [altezzaHeader, altezzaFooter, fromSaluteSimplex]);

  // Fix accessibilità
  useEffect(() => {
    const autocomplete = document.getElementsByClassName("MuiAutocomplete-input");
    if (autocomplete.length > 0) {
      for (let i = 0; i < autocomplete.length; i++) {
        autocomplete[i].removeAttribute("role");
      }
    }
  }, []);

  const validationSchema = yup.object({
    [ID_PAZIENTE]: yup
      .string()
      .required(getMessageString(intl, REQUIRED_FIELD))
      .test({
        message: getMessageString(intl, CODICE_ACCESSO_ERROR) + config[CIFRE_ID_PAZIENTE_JSON] + getMessageString(intl, CODICE_ACCESSO_ERROR_2),
        test: (value) => value.length === config[CIFRE_ID_PAZIENTE_JSON],
      }),
  });

  const formik = useFormik({
    initialValues: {
      [ID_PAZIENTE]: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dispatch(resetNotification());
      setShowSpinner(true);
      if (notificationPermission === NOTIFICHE_NON_IMPOSTATE) requestPermission(values);
      else dispatch(fetchMonitoraggio(values));
    },
  });

  useEffect(() => {
    if (monitoraggioStarted) {
      setShowSpinner(false);
      if (monitoraggio) {
        navigate(PATH_MONITORAGGIO);
        sessionStorage.setItem(IS_NAVIGATION_FROM_FORM, true);
        window.ReactNativeWebView?.postMessage(JSON.stringify({ monitoraggio: monitoraggio[CODICE_EVENTO] }));
      }
    }
    // eslint-disable-next-line
  }, [monitoraggio, monitoraggioStarted]);

  const requestPermission = async (values) => {
    //requesting permission using Notification API
    if ("Notification" in window) {
      window.Notification?.requestPermission().then((permission) => {
        setNotificationPermission(permission);
        if (permission !== NOTIFICHE_NON_IMPOSTATE) {
          if (permission === NOTIFICHE_CONCESSE) getToken(firebaseMessaging, { vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY });
          dispatch(fetchMonitoraggio(values));
          localStorage.setItem(NOTIFICATION_PERMISSION_SETTED, true);
        }
      });
    } else setNotificationPermission(NOTIFICHE_NEGATE);
    setShowSpinner(false);
  };

  return (
    <Box className="home" sx={{ height: pageHeight }}>
      <Banners />
      <GuidaInstallazione />
      <Card>
        <form onSubmit={formik.handleSubmit}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography>{getMessageString(intl, HOME_INFO)}</Typography>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={getMessageString(intl, CODICE_ACCESSO_PLACEHOLDER)}
                  variant="outlined"
                  value={formik.values[ID_PAZIENTE]}
                  onChange={(e) => {
                    const value = e.target.value;
                    const regex = /^[A-Z|\-0-9]*$/i;
                    if (regex.test(value) && value.length <= config[CIFRE_ID_PAZIENTE_JSON]) formik.setFieldValue(ID_PAZIENTE, value.toUpperCase());
                  }}
                  {...(formik.values[ID_PAZIENTE]?.length > 0 && {
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={getMessageString(intl, CLEAR)}
                            edge="end"
                            onClick={() => formik.setFieldValue(ID_PAZIENTE, "")}
                            {...(formik.errors[ID_PAZIENTE] != null && { sx: { color: "#d32f2f" } })}
                          >
                            <CancelIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                    autoFocus: true,
                  })}
                  {...((formik.values[ID_PAZIENTE]?.length > 0 || formik.touched[ID_PAZIENTE]) &&
                    formik.errors[ID_PAZIENTE] != null && {
                      error: true,
                      helperText: formik.errors[ID_PAZIENTE],
                      sx: { "& input": { color: "#d32f2f" } },
                    })}
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button type="submit">{getMessageString(intl, CERCA)}</Button>
          </CardActions>
        </form>
      </Card>
      {notificationPermission === NOTIFICHE_NEGATE && (
        <Alert variant="outlined" severity="warning">
          <Typography>{getMessageString(intl, NOTIFICHE_NEGATE_ALERT)}</Typography>
        </Alert>
      )}
      <WelcomePage
        modalOpen={showWelcomeModal}
        onEnter={() => {
          localStorage.setItem(WELCOME_PAGE_VISITED, true);
          setShowWelcomeModal(false);
        }}
      />
      <LoadingSpinner loading={showSpinner} />
    </Box>
  );
});
